// Console SPA — API 客户端。
//
// 与 captcha-console Worker 对接的最小封装：
//   - 自动注入 Bearer token（来自 storage.readSession()）
//   - 401 / session_invalid 时清除本地会话并跳转 #/login
//   - 解析 JSON，错误情况统一抛 ApiError

import { clearSession, readSession, writeSession, type StoredSession } from './storage';

export class ApiError extends Error {
  public readonly status: number;
  public readonly code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    if (code !== undefined) this.code = code;
  }
}

// 生产构建时由 vite 注入 VITE_API_BASE 指向 console-api worker
// 开发时为空 → vite proxy 把 /api 转发到 wrangler dev
const BASE = (import.meta as unknown as { env: { VITE_API_BASE?: string } }).env.VITE_API_BASE ?? '';

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** 不带 Bearer 的开放接口（login / register 用） */
  anonymous?: boolean;
}

async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const sess = readSession();
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (!opts.anonymous && sess !== null) headers.authorization = `Bearer ${sess.token}`;

  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'omit',
  };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);

  const resp = await fetch(BASE + path, init);

  // session 刷新 — captcha-console 在 refresh 窗口下发新 JWT
  const refreshed = resp.headers.get('x-session-refresh');
  if (refreshed && sess !== null) {
    writeSession({ ...sess, token: refreshed });
  }

  if (resp.status === 204) return undefined as unknown as T;

  let body: unknown;
  try {
    body = await resp.json();
  } catch {
    body = null;
  }

  if (!resp.ok) {
    const obj = (body ?? {}) as Record<string, unknown>;
    const code = typeof obj.error_code === 'string' ? obj.error_code : undefined;
    let msg = typeof obj.message === 'string' ? obj.message : `http ${resp.status}`;
    // 字段级校验错误 — staging 阶段后端把具体字段挂在 _validation
    const v = obj._validation as { field?: string; message?: string } | undefined;
    if (v && typeof v.field === 'string' && typeof v.message === 'string') {
      msg = `${v.field}: ${v.message}`;
    }
    if (resp.status === 401 || code === 'SESSION_INVALID') {
      clearSession();
      window.location.hash = '#/login';
    }
    throw new ApiError(resp.status, msg, code);
  }
  return body as T;
}

// ---------------------------------------------------------------------------
// 具体接口
// ---------------------------------------------------------------------------

export interface LoginSuccessResp {
  success: true;
  trace_id: string;
  session_id: string;
  jwt: string;
  expires_at: number;
  refresh_at: number;
  user_id: string;
  tenant_id: string;
}

export interface LoginTotpRequiredResp {
  success: true;
  trace_id: string;
  requires_totp: true;
  totp_pending_token: string;
  expires_at: number;
}

export type LoginResp = LoginSuccessResp | LoginTotpRequiredResp;

export async function login(email: string, password: string): Promise<LoginResp> {
  return api<LoginResp>('/api/v1/auth/login', {
    method: 'POST',
    body: { email, password },
    anonymous: true,
  });
}

export interface RegisterResp {
  success: true;
  trace_id: string;
  tenant_id: string;
  user_id: string;
  email_verification: 'pending';
}

export async function register(input: {
  email: string;
  password: string;
  company: string;
}): Promise<RegisterResp> {
  return api<RegisterResp>('/api/v1/auth/register', {
    method: 'POST',
    body: input,
    anonymous: true,
  });
}

/** OAuth 起点 URL — 直接 window.location 跳转，由 console-api 302 到 provider。 */
export function oauthStartUrl(provider: 'google' | 'github'): string {
  return `${BASE}/api/v1/auth/oauth/${provider}/start`;
}

export interface SiteRecord {
  id: string;
  name: string;
  site_key: string;
  secret_key_mask: string;
  allowed_origins: string[];
  challenge_priority: string[];
  risk_thresholds: { pass: number; deny: number };
  status: 'active' | 'revoked';
  created_at: number;
}

export interface ListSitesResp {
  success: true;
  trace_id: string;
  sites: SiteRecord[];
}

export interface CreateSiteResp {
  success: true;
  trace_id: string;
  site: SiteRecord;
  secret_key_plaintext: string;
  _warning: string;
}

export async function listSites(): Promise<ListSitesResp> {
  return api<ListSitesResp>('/api/v1/sites');
}

export async function createSite(input: {
  name: string;
  allowed_origins: string[];
  challenge_priority?: string[];
  risk_thresholds?: { pass: number; deny: number };
}): Promise<CreateSiteResp> {
  return api<CreateSiteResp>('/api/v1/sites', { method: 'POST', body: input });
}

export interface RotateSecretResp {
  success: true;
  trace_id: string;
  site_id: string;
  secret_key_plaintext: string;
  secret_key_mask: string;
  old_secret_expires_at: number;
}

export async function rotateSecret(id: string): Promise<RotateSecretResp> {
  return api<RotateSecretResp>(`/api/v1/sites/${id}/rotate-secret`, { method: 'POST' });
}

export async function deleteSite(id: string): Promise<void> {
  return api<void>(`/api/v1/sites/${id}`, { method: 'DELETE' });
}

export interface DashboardWindow {
  /** 1h / 24h / 7d / 30d */
  range: '1h' | '24h' | '7d' | '30d';
}

export interface DashboardMetrics {
  success: true;
  trace_id: string;
  range: string;
  total: number;
  pass: number;
  challenge: number;
  deny: number;
  pass_rate: number;
  challenge_rate: number;
  deny_rate: number;
  /** 最近 1 小时数据延迟 ≤ 60s 提示 */
  realtime_lag_sec?: number;
}

export async function getDashboardMetrics(range: DashboardWindow['range']): Promise<DashboardMetrics> {
  return api<DashboardMetrics>(`/api/v1/dashboard/metrics?range=${encodeURIComponent(range)}`);
}

export interface CallLogEntry {
  trace_id: string;
  ts: number;
  site_key: string;
  ip_prefix: string;
  challenge_id?: string;
  bot_score?: number;
  rule_score?: number;
  final_score?: number;
  risk_level: 'pass' | 'challenge' | 'deny';
  error_code?: string;
}

export interface CallLogQuery {
  range_from?: number;
  range_to?: number;
  site_key?: string;
  ip_prefix?: string;
  challenge_id?: string;
  risk_level?: 'pass' | 'challenge' | 'deny';
  cursor?: string;
  limit?: number;
}

export interface LogsResp {
  success: true;
  trace_id: string;
  items: CallLogEntry[];
  truncated: boolean;
  next_cursor?: string;
}

export async function queryLogs(q: CallLogQuery): Promise<LogsResp> {
  const usp = new URLSearchParams();
  if (q.range_from !== undefined) usp.set('range_from', String(q.range_from));
  if (q.range_to !== undefined) usp.set('range_to', String(q.range_to));
  if (q.site_key !== undefined) usp.set('site_key', q.site_key);
  if (q.ip_prefix !== undefined) usp.set('ip_prefix', q.ip_prefix);
  if (q.challenge_id !== undefined) usp.set('challenge_id', q.challenge_id);
  if (q.risk_level !== undefined) usp.set('risk_level', q.risk_level);
  if (q.cursor !== undefined) usp.set('cursor', q.cursor);
  if (q.limit !== undefined) usp.set('limit', String(q.limit));
  return api<LogsResp>('/api/v1/logs?' + usp.toString());
}

export type { StoredSession };
